import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalComponent, NotificationComponent } from '@epsilon/core-ui';
import { UntilDestroy } from '@ngneat/until-destroy';

import { AudienceDefinitionGenerateAIRating } from '../../audience-builder/audience-builder.models';
import { AudienceBuilderService } from '../../audience-builder/audience-builder.service';
import { CabConstants } from '../../cab.constants';
import { ConfirmationPopupComponent } from '../../shared/components/confirmation-popup/confirmation-popup.component';
import { AudienceAIChatService } from '../audience-ai-chat.service';
import {
  AudienceAIChatRequest,
  AudienceAIChatResponse,
  AudienceAIResponseType,
  BaseMessage,
  Conversation,
  Feedback,
  JobResponse,
  JobType,
  UserType
} from '../audience-ai.model';
import { UtilsService } from '../../utils/utilservice';
import { AUDIENCE_AI_DAILOG_TXT, DEFINITION_TXT } from '../../utils/utils';
import { lastValueFrom } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'lib-audience-ai-dailog',
    templateUrl: './audience-ai-dailog.component.html',
    styleUrls: ['./audience-ai-dailog.component.sass'],
    standalone: false
})
export class AudienceAIDailogComponent
  implements OnInit, AfterViewChecked, AfterViewInit
{
  @ViewChild('newDefinitionModal') public newDefinitionModal;
  isQueryRunning = true;
  audienceAIChatRequest: AudienceAIChatRequest;
  chatConversations: Conversation;
  audienceAIFormGroup: UntypedFormGroup;
  contextId: string;
  confirmationTitle = 'Restart the Audience AI definition';
  customMessage =
    'Restarting the Audience AI definition will cause you to lose all progress. Are you sure you want to continue?';
  confirmText = 'Continue';
  cancelText = 'Cancel';
  isUserInputValid = true;
  savedAudienceDefinitionId: string;
  dataUniverseId: string;
  @ViewChild('chatMessages') public chatContainer!: ElementRef | undefined;
  @ViewChild('inputTextArea', { static: false }) inputTextArea!: ElementRef;
  @ViewChild('restartConfirmationPopup')
  restartConfirmationPopup!: ConfirmationPopupComponent;
  @ViewChild('backConfirmationPopup')
  backConfirmationPopup!: ConfirmationPopupComponent;
  @ViewChild('toastError', { static: true })
  public toastError: NotificationComponent;
  public previewContainer!: ElementRef
  errorMessage: string;
  conversationId: string;
  userSessionId: string;
  conversation: any;
  count: string;
  isCountCancelled = false;
  isError = false;
  showCancel = false;
  jobType: string;
  sample_data: { [key: string]: any }[];
  chatContentLoaded = false;
  isScrollAtBottom = true;
  scrollTimeout = null;
  notificationStyle: string;
  notificationMessage: string;
  currentChat: BaseMessage;
  feedbackSuccessMessage = 'Your feedback has been submitted.';
  feedbackErrorMessage = 'There is an error while submitting the feedback.';
  countCancelledMessage =
    "Run count has been canceled. Let me know when you're ready to run it again.";
  countErrorMessage = 'Failed to fetch the Count.';
  previewCancelledMessage =
    "Previewing audience definition has been canceled. Let me know when you're ready to run it again.";
  previewErrorMessage = 'Failed to fetch the Sample Data';
  showSaveWarning = false;
  hasResponseError = false;
  previewCollapsed = false;
  selectedPickList: string;

  constructor(
    public audienceAIChatService: AudienceAIChatService,
    public builderService: AudienceBuilderService,
    public utilsService: UtilsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    setTimeout(() => {
      this.newDefinitionModal.show();
    });
    this.contextId = this.route.snapshot.paramMap.get('contextId');
    this.dataUniverseId = this.route.snapshot.paramMap.get('dataUniverseId');
    this.conversationId = this.route.snapshot.paramMap.get('conversationId');
    this.userSessionId = localStorage.getItem('userSessionId');
    localStorage.removeItem('userSessionId');
    this.initialize();
    this.onPageReload();
  }

  ngAfterViewChecked(): void {
    if (
      this.chatContainer &&
      this.previewContainer &&
      !this.chatContentLoaded
    ) {
      this.scrollToBottom(this.chatContainer);
      this.scrollToBottom(this.previewContainer);
      this.chatContentLoaded = true;
    }
  }

  ngAfterViewInit() {
    this.inputTextArea?.['element'].nativeElement.focus();
  }

  private initialize(): void {
    this.audienceAIChatRequest = new AudienceAIChatRequest();
    this.audienceAIChatRequest.data_universe_id = this.dataUniverseId;
    this.audienceAIChatRequest.business_unit_id = this.getBusinessUnitId();
    this.chatConversations = new Conversation();
    this.audienceAIFormGroup = this.buildAudienceAIFormGroup();
    this.isUserInputEnable();
  }

  private buildAudienceAIFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      chatInput: new UntypedFormControl(''),
      previewData: this.initPreviewDataFormGroup(),
      commentMessage: new UntypedFormControl('')
    });
  }

  private getBusinessUnitId() {
    const businessUnitId = this.utilsService.getBusinessUnitId(this.route);
    return businessUnitId != 'DEFAULT' ? businessUnitId : null;
  }

  private onPageReload(): void {
    if (sessionStorage.getItem('conversationType')) {
      this.initiateChat();
    } else {
      this.getConversation();
    }
  }

  private getConversation(): void {
    this.isQueryRunning = true;
    this.chatConversations.chat_history = [];
    const url = this.route.snapshot.url.join('/');
    this.userSessionId = url.split('/').pop();
    this.audienceAIChatService
      .getConversation(this.contextId, this.userSessionId)
      .subscribe({
        next: (response: Conversation) => {
          this.isError = false;
          response.chat_history.forEach((message) => {
            if (
              message.response_metadata.response_type ===
              AudienceAIResponseType.ERROR
            )
              this.chatConversations.chat_history.pop();
            else {
              if (message.type == UserType.ASSISTANT) {
                message.content = this.getChatMessage(message);
              }
              this.buildChatConversations(message);
            }
          });
          const currentDate = this.getCurrentDate();
          const prevDate = new Date(
            response.chat_history[
              response.chat_history.length - 1
            ].additional_kwargs.created_at
          );
          if (
            response.chat_history.length > 2 &&
            currentDate.toDateString() !== prevDate.toDateString()
          ) {
            const welcomeMessage = this.addWelcomeMessage(response.user_id);
            this.buildChatConversations(welcomeMessage);
          }

          this.scrollToBottom(this.chatContainer);
          this.count = response.conversation_state.audience_count?.['count'];
          const previewDataFormGroup = this.buildPreviewDataFormGroup(
            response.conversation_state.audience_preview
          );
          this.audienceAIFormGroup.setControl(
            'previewData',
            previewDataFormGroup
          );
          this.isQueryRunning = false;
          this.isUserInputEnable();
          this.hasResponseError = this.displayErrorMessage(
            this.chatConversations.chat_history[-1]
          );
          setTimeout(() => {
            this.inputTextArea['element'].nativeElement.focus();
          }, 0);
        },
        error: (error) => {
          console.error('Error fetching all the conversations', error);
          this.isQueryRunning = false;
          this.isError = true;
        }
      });
  }

  private getChatMessage(message: BaseMessage) {
    const aiMessageContent = JSON.parse(message.content);
    let chatMessage = aiMessageContent['chat_message'];
    if (message.additional_kwargs.audience_definition_id) {
      chatMessage = this.updateChatContent(
        aiMessageContent['chat_message'],
        message.additional_kwargs.audience_definition_id
      );
    } else if (message.additional_kwargs.count_job_id) {
      const count = message.additional_kwargs['count'];
      if (count != null) {
        chatMessage = `We found <b>${count.toLocaleString()}</b> individuals matching your query.`;
      } else {
        chatMessage = this.countCancelledMessage;
      }
    } else if (message.additional_kwargs.preview_job_id) {
      chatMessage = `We have successfully generated the preview data.`;
    }
    aiMessageContent['chat_message'] = chatMessage;
    return aiMessageContent;
  }

  private addWelcomeMessage(userId: string): BaseMessage {
    return {
      type: UserType.ASSISTANT,
      content: {
        chat_message: `Welcome back ${userId.split('@')[0]}`
      },
      additional_kwargs: {
        created_at: this.getCurrentDate()
      },
      response_metadata: {
        response_type: 'WELCOME_BACK'
      }
    } as BaseMessage;
  }

  public closeModal(modal: ModalComponent) {
    this.router
      .navigate([
        this.utilsService.getProductBaseUrl(this.router, this.route),
        this.contextId,
        'definition'
      ]);
    modal.hide();
  }

  showModal(modal: ModalComponent, chatMessage: BaseMessage) {
    this.currentChat = chatMessage;
    modal.show();
  }

  public goToNewAudienceAIChatDailog(userSessionId: string): void {
    this.audienceAIChatService
      .newConversation(
        this.contextId,
        this.dataUniverseId,
        userSessionId,
        this.getBusinessUnitId()
      )
      .subscribe({
        next: (response: Conversation) => {
          this.conversation = response;
          this.conversationId = this.conversation.id;
          this.userSessionId = this.conversation.userSessionId;
          this.router
            .navigate([
              this.utilsService.getProductBaseUrl(this.router, this.route),
              this.contextId,
              this.dataUniverseId,
              AUDIENCE_AI_DAILOG_TXT,
              this.conversationId
            ])
            .then(() => {
              this.initiateChat();
            });
        },
        error: (error) => {
          console.error('Error fetching conversations', error);
          this.errorMessage = 'Server Error. Please try after sometime.';
          this.toastError.show();
        }
      });
  }

  private initPreviewDataFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      audience_name: new UntypedFormControl(''),
      description: new UntypedFormControl(''),
      channel: new UntypedFormControl(''),
      audience_type: new UntypedFormControl(''),
      dedupe: new UntypedFormControl(''),
      audience_query: new UntypedFormControl('')
    });
  }

  private performHumanMessage(userMessage: string): void {
    if(this.selectedPickList){
      userMessage = this.selectedPickList + ".<br>" + userMessage;
      this.removePickListValue();
    }
    const chatMessage: BaseMessage = {
      type: UserType.HUMAN,
      content: userMessage,
      additional_kwargs: {
        created_at: this.getCurrentDate()
      },
      response_metadata: {
        response_type: AudienceAIResponseType.OK
      }
    };
    const chatHistory = this.chatConversations.chat_history;
    if (
      chatHistory[chatHistory.length - 1]?.response_metadata.response_type ===
      'WELCOME_BACK'
    ) {
      chatHistory.pop();
    }
    this.buildChatConversations(chatMessage);
    this.audienceAIChatRequest.input_message = userMessage;
    this.performChat(this.audienceAIChatRequest);
    this.scrollToBottom(this.chatContainer);
  }

  public initiateChat(): void {
    this.performHumanMessage(CabConstants.AUDIENCE_AI_CHAT_INITIATE_MESSAGE);
    sessionStorage.removeItem('conversationType');
    setTimeout(() => {
      this.inputTextArea['element'].nativeElement.focus();
    }, 0);
  }

  public getInputContext() {
    return {
      placeholderText: 'Message Audience AI',
      audienceAIFormGroup: this.audienceAIFormGroup,
      isQueryRunning: this.isQueryRunning,
      isUserInputValid: this.isUserInputValid,
      submitQuery: this.submitQuery.bind(this)
    };
  }

  public submitQuery(): void {
    if (
      !this.audienceAIFormGroup.get('chatInput').value ||
      this.audienceAIFormGroup.get('chatInput').value.trim().length === 0 ||
      this.audienceAIFormGroup.get('chatInput').errors ||
      this.isQueryRunning
    ) {
      return;
    }
    this.performHumanMessage(this.audienceAIFormGroup.get('chatInput').value);
    this.audienceAIFormGroup.get('chatInput').reset();
    this.adjustInputHeight(this.inputTextArea['element'].nativeElement);
  }

  private buildChatConversations(chatMessage: BaseMessage) {
    this.chatConversations.chat_history.push(chatMessage);
    this.showConfirmationModal();
  }

  public scrollToBottom(container: ElementRef): void {
    if (this.chatContainer && this.previewContainer) {
      setTimeout(() => {
        container.nativeElement.scrollTo({
          top: this.chatContainer.nativeElement.scrollHeight,
          behavior: 'smooth'
        });
        this.isScrollAtBottom = true;
      }, 0);
    }
  }

  public onScroll(container: ElementRef) {
    const chatContainer = container.nativeElement;
    const threshold = 25;
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.isScrollAtBottom =
        chatContainer.scrollHeight - chatContainer.scrollTop <=
        chatContainer.clientHeight + threshold;
    }, 300);
  }

  public clearChat(): void {
    this.audienceAIChatRequest = new AudienceAIChatRequest();
    this.audienceAIChatRequest.data_universe_id = this.dataUniverseId;
    this.audienceAIChatRequest.business_unit_id = this.getBusinessUnitId();
    this.chatConversations = new Conversation();
    this.audienceAIFormGroup.reset();
    this.audienceAIFormGroup.setControl(
      'previewData',
      new UntypedFormGroup({})
    );
    this.count = null;
    this.isQueryRunning = false;
    this.audienceAIChatService
      .finishConversation(this.contextId, this.conversationId)
      .subscribe(() => {
        this.goToNewAudienceAIChatDailog(this.userSessionId);
      });
  }

  public selectOption(option: string): void {
    this.audienceAIFormGroup.get('chatInput').patchValue(option);
    this.submitQuery();
  }

  public displayErrorMessage(chat: BaseMessage): boolean {
    const isLastChat =
      this.chatConversations.chat_history[
      this.chatConversations.chat_history.length - 1
      ] === chat;
    return (
      chat?.response_metadata?.response_type === AudienceAIResponseType.ERROR &&
      isLastChat
    );
  }

  public onRegenerate() {
    const chatHistory = this.chatConversations.chat_history;
    if (chatHistory.length === 0) return;
    chatHistory.pop();
    this.audienceAIChatRequest.input_message =
      chatHistory[chatHistory.length - 1].content;
    this.performChat(this.audienceAIChatRequest);
    this.scrollToBottom(this.chatContainer);
  }

  private performChat(audienceAIChatRequest: AudienceAIChatRequest) {
    this.isQueryRunning = true;
    this.audienceAIChatService
      .performChat(this.contextId, audienceAIChatRequest, this.conversationId)
      .subscribe({
        next: async (response: AudienceAIChatResponse) => {
          if (
            response.ai_message.response_metadata.response_type ===
            AudienceAIResponseType.OK
          ) {
            this.hasResponseError = false;
            const aiMessageContent = JSON.parse(response.ai_message.content);
            if (
              response.ai_message.additional_kwargs.count_job_id ||
              response.ai_message.additional_kwargs.preview_job_id
            ) {
              this.jobType = response.ai_message.additional_kwargs.count_job_id
                ? JobType.COUNT
                : JobType.PREVIEW;
              await this.processJobResponse(response);
            } else {
              if (
                response.ai_message.additional_kwargs.audience_definition_id
              ) {
                aiMessageContent['chat_message'] = this.updateChatContent(
                  aiMessageContent['chat_message'],
                  response.ai_message.additional_kwargs.audience_definition_id
                );
              }
              const chatMessage: BaseMessage = {
                type: UserType.ASSISTANT,
                content: aiMessageContent,
                additional_kwargs: response.ai_message.additional_kwargs,
                response_metadata: response.ai_message.response_metadata
              };
              this.buildChatConversations(chatMessage);
              this.count =
                response.conversation_state.audience_count?.['count'];
            }
          } else if (
            response.ai_message.response_metadata.response_type ===
            AudienceAIResponseType.ERROR
          ) {
            this.hasResponseError = true;
            const chatMessage: BaseMessage = {
              type: UserType.ASSISTANT,
              content: JSON.parse(response.ai_message.content),
              additional_kwargs: response.ai_message.additional_kwargs,
              response_metadata: response.ai_message.response_metadata
            };
            chatMessage.content['chat_message'] = 'There was an unexpected error';
            this.buildChatConversations(chatMessage);
          }
          const previewDataFormGroup = this.buildPreviewDataFormGroup(
            response.conversation_state.audience_preview
          );
          this.scrollToBottom(this.chatContainer);
          this.scrollToBottom(this.previewContainer);
          this.audienceAIFormGroup.setControl(
            'previewData',
            previewDataFormGroup
          );
          this.isQueryRunning = false;
          this.isUserInputEnable();
        },
        error: () => {
          this.hasResponseError = true;
          const chatMessage: BaseMessage = {
            type: UserType.ASSISTANT,
            content: {
              chat_message:
                'Apologies, Server error occurred! Can not initiate Audience AI'
            },
            response_metadata: { response_type: AudienceAIResponseType.ERROR },
            additional_kwargs: {
              created_at: this.getCurrentDate()
            }
          };
          if (this.chatConversations.chat_history.length >= 2) {
            chatMessage.content[
              'chat_message'
            ] = `There was an unexpected error\n`;
          }
          this.buildChatConversations(chatMessage);
          this.scrollToBottom(this.chatContainer);
          this.scrollToBottom(this.previewContainer);
          this.isQueryRunning = false;
          this.isUserInputEnable();
        }
      });
  }

  public addSQLTags(sql: string) {
    return '```sql\n' + sql + '\n```';
  }

  private updateChatContent(message: string, audienceId: string) {
    let baseUrl = window.location.origin;
    const currentUrl = this.router.url;
    baseUrl =
      this.utilsService.environmentValue('domainName') === 'local'
        ? `${baseUrl}/app`
        : baseUrl;
    const audienceBuilderUrl = `${baseUrl}${currentUrl.split('ai-dailog')[0]
      }builder/edit/${audienceId}`;
    const link = `<a href="${audienceBuilderUrl}" target="_blank">Edit audience</a>`;
    message = message.replace('SAVED_AUDIENCE_CH', link);
    return message;
  }

  private buildPreviewDataFormGroup(data: any): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({});
    for (const key in data) {
      if (data[key]) {
        formGroup.addControl(
          key,
          new UntypedFormControl({ value: data[key], disabled: false })
        );
      }
    }
    return formGroup;
  }

  public selectFeedback(userRating: number, chatIndex: number): void {
    const feedback =
      this.chatConversations.chat_history[chatIndex]?.additional_kwargs
        ?.feedback ?? new Feedback();
    feedback.currentFeedbackRating = userRating;
    feedback.isFeedbackCommentGiven = false;
    this.chatConversations.chat_history[chatIndex].additional_kwargs.feedback =
      feedback;
    if (this.chatConversations.chat_history.length - 1 === chatIndex) {
      this.scrollToBottom(this.chatContainer);
    }
  }

  private showFeedbackNotification(
    chat: BaseMessage,
    notificationStyle: string,
    notificationMessage: string
  ) {
    this.notificationStyle = notificationStyle;
    this.notificationMessage = notificationMessage;
    chat.additional_kwargs.feedback.showNotification = true;
    setTimeout(
      () => (chat.additional_kwargs.feedback.showNotification = false),
      4000
    );
  }

  public submitFeedback(chatIndex: number): void {
    const chat = this.chatConversations.chat_history[chatIndex];
    const generateFromTextRequest: AudienceDefinitionGenerateAIRating = {
      cabContextId: this.route.snapshot.paramMap.get('contextId'),
      genAIRequestId: chat.additional_kwargs.genai_request_id,
      userRating: chat.additional_kwargs?.feedback?.currentFeedbackRating,
      userComments: this.audienceAIFormGroup.get('commentMessage').value
    };
    this.builderService
      .generateAudienceDefinitionGenAIQueryRating(generateFromTextRequest)
      .subscribe({
        next: () => {
          this.audienceAIFormGroup.get('commentMessage').reset();
          this.showFeedbackNotification(
            chat,
            'success',
            this.feedbackSuccessMessage
          );
          chat.additional_kwargs.feedback.isFeedbackCommentGiven = true;
          chat.additional_kwargs.feedback.previousFeedbackRating =
            chat.additional_kwargs?.feedback?.currentFeedbackRating;
        },
        error: () => {
          this.showFeedbackNotification(
            chat,
            'error',
            this.feedbackSuccessMessage
          );
          chat.additional_kwargs.feedback.isFeedbackCommentGiven = true;
        }
      });
  }

  private async processJobResponse(response: AudienceAIChatResponse) {
    try {
      const jobId =
        this.jobType === JobType.PREVIEW
          ? response.conversation_state.preview_job_id
          : response.conversation_state.count_job_id;

      this.showCancel = true;
      const result: JobResponse = await lastValueFrom(
        this.audienceAIChatService.pollJobStatus(jobId, this.contextId)
      );
      this.showCancel = false;

      if (result.status === 'SUCCESS') {
        if (this.jobType === JobType.PREVIEW) {
          this.handlePreviewSuccess(response, result);
        } else {
          this.handleCountSuccess(response, result);
        }
        this.updateChatHistory(response, this.sample_data);
      } else {
        this.handleError(response);
      }
    } catch (error) {
      console.error('Error fetching job status:', error);
      this.handleError(response);
    }
  }

  handlePreviewSuccess(
    response: AudienceAIChatResponse,
    result: JobResponse
  ): void {
    const aiMessageContent = JSON.parse(response.ai_message.content);
    this.sample_data = result.result['data'];
    if (this.sample_data && this.sample_data.length > 0) {
      aiMessageContent[
        'chat_message'
      ] = `Here's a preview of profiles in this audience:<br><br>`;
    } else {
      aiMessageContent[
        'chat_message'
      ] = `Sorry, I did not find any sample data matching your specified criteria. Please review your input and try again.`;
    }
    response.ai_message.content = JSON.stringify(aiMessageContent);
  }

  public cancelJob() {
    this.isCountCancelled = true;
    this.audienceAIChatService.cancelPolling();
    this.showCancel = false;
  }

  // Add a getter for headers if you don't already have one
  get tableHeaders(): string[] {
    return this.sample_data && this.sample_data.length > 0
      ? Object.keys(this.sample_data[0])
      : [];
  }

  private handleCountSuccess(
    response: AudienceAIChatResponse,
    result: JobResponse
  ): void {
    const aiMessageContent = JSON.parse(response.ai_message.content);
    this.count = result.result['count'];
    aiMessageContent[
      'chat_message'
    ] = `We found <b>${this.count.toLocaleString()}</b> individuals matching your query.`;
    response.ai_message.content = JSON.stringify(aiMessageContent);
    this.audienceAIChatService
      .updateCount(this.contextId, this.conversationId, this.count)
      .subscribe();
  }

  private handleError(response: AudienceAIChatResponse): void {
    const aiMessageContent = JSON.parse(response.ai_message.content);
    let responseType: any;
    if (this.jobType == JobType.COUNT) {
      aiMessageContent['chat_message'] = this.isCountCancelled
        ? this.countCancelledMessage
        : this.countErrorMessage;
      responseType = this.isCountCancelled
        ? AudienceAIResponseType.OK
        : AudienceAIResponseType.ERROR;
    } else {
      aiMessageContent['chat_message'] = this.isCountCancelled
        ? this.previewCancelledMessage
        : this.previewErrorMessage;
      responseType = this.isCountCancelled
        ? AudienceAIResponseType.OK
        : AudienceAIResponseType.ERROR;
    }
    aiMessageContent['options'].push('Try again');
    const chatMessageConversation: BaseMessage = {
      type: UserType.ASSISTANT,
      content: aiMessageContent,
      response_metadata: { response_type: responseType },
      additional_kwargs: {
        created_at: new Date()
      }
    };
    this.buildChatConversations(chatMessageConversation);
  }

  private updateChatHistory(
    response: AudienceAIChatResponse,
    sampleData?: any[]
  ): void {
    const aiMessageContent = JSON.parse(response.ai_message.content);
    const chatMessageConversation: BaseMessage = {
      type: UserType.ASSISTANT,
      content: aiMessageContent,
      additional_kwargs: {
        ...response.ai_message.additional_kwargs,
        sampleData: sampleData ? [...sampleData] : [],
        tableHeaders:
          sampleData && sampleData.length > 0 ? Object.keys(sampleData[0]) : []
      },
      response_metadata: response.ai_message.response_metadata
    };
    this.buildChatConversations(chatMessageConversation);
  }

  public openRestartConfirmationPopup(): void {
    this.restartConfirmationPopup.launchAlertModal();
  }

  private openBackConfirmationPopup(): void {
    this.backConfirmationPopup.launchAlertModal();
  }

  private showConfirmationModal(): void {
    if (
      !this.chatConversations ||
      !this.chatConversations.chat_history ||
      this.chatConversations.chat_history.length === 0
    ) {
      this.showSaveWarning = false;
      return;
    }

    const chatHistory = this.chatConversations.chat_history;
    const latestMessage = chatHistory[chatHistory.length - 1];

    this.showSaveWarning =
      latestMessage.content['options']?.some((opt) =>
        opt.toLowerCase().includes('save')
      ) ?? false;
  }

  public handleBackAction(): void {
    if (this.showSaveWarning) {
      this.openBackConfirmationPopup();
    } else {
      this.router.navigate([
        this.utilsService.getProductBaseUrl(this.router, this.route),
        this.contextId,
        DEFINITION_TXT
      ]);
    }
  }

  public handleRestartConfirm(): void {
    this.clearChat();
  }

  public handleCancel(): void {
    return;
  }

  public handleBackConfirm() {
    this.router.navigate([
      this.utilsService.getProductBaseUrl(this.router, this.route),
      this.contextId,
      DEFINITION_TXT
    ]);
  }

  public onChatInputChange(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.isUserInputEnable();
    this.adjustInputHeight(textarea);
  }

  public isFeedbackMenuShown(genAIRequestId: string): boolean {
    return genAIRequestId ? true : false;
  }

  public isFeedbackSelected(index: number, rating: number): boolean {
    return this.chatConversations.chat_history[index].additional_kwargs
      ?.feedback?.currentFeedbackRating === rating
      ? true
      : false;
  }

  public isCommentAreaShown(index: number): boolean {
    return (
      this.chatConversations.chat_history[index].additional_kwargs?.feedback
        ?.currentFeedbackRating !== 0 &&
      this.chatConversations.chat_history[index].additional_kwargs?.feedback
        ?.isFeedbackCommentGiven === false
    );
  }

  public close(index: number): void {
    const chat = this.chatConversations.chat_history[index];
    chat.additional_kwargs.feedback.isFeedbackCommentGiven = true;
    chat.additional_kwargs.feedback.currentFeedbackRating =
      chat.additional_kwargs?.feedback?.previousFeedbackRating;
  }

  public adjustInputHeight(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'fit-content';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        return;
      } else {
        event.preventDefault();
        this.submitQuery();
      }
    }
  }

  private isUserInputEnable(): void {
    const inputValue = this.audienceAIFormGroup.get('chatInput');
    this.isUserInputValid =
      this.isQueryRunning ||
      !inputValue ||
      !inputValue.value ||
      inputValue.value.trim().length === 0;
  }

  public dateGrouping(chat: BaseMessage, i: number): boolean {
    if (chat.additional_kwargs.created_at !== null) {
      if (i === 1 && chat.type === UserType.ASSISTANT) return true;
      const prevChat = this.chatConversations.chat_history[i - 1];
      const prevDate = new Date(prevChat.additional_kwargs?.created_at);
      const chatDate = new Date(chat.additional_kwargs?.created_at);
      return chatDate.toDateString() !== prevDate.toDateString();
    } else {
      return false;
    }
  }

  private getCurrentDate(): Date {
    return new Date(new Date().toISOString());
  }

  public copyToClipboard(): void {
    navigator.clipboard.writeText(
      this.audienceAIFormGroup.get('previewData').value.audience_query
    );
  }

  public emitPickListValues(event: string) {
    this.selectedPickList = event;
  }

  public removePickListValue() {
    this.selectedPickList = null;
  }
}
